import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { UploadCloud, FileSpreadsheet, AlertTriangle, CheckCircle2, Download, ArrowLeft, RefreshCw, History, Trash2, ShieldAlert } from "lucide-react";

import { DuplicadosRoge } from "@/components/DuplicadosRoge";
import { EmpresaUsuariaSelect, useEmpresasUsuarias, PLANTA_TEXTO } from "@/components/EmpresaUsuariaSelect";

function EmpresaFila({ r, sel, setSel }) {
  const lista = useEmpresasUsuarias();
  const actual = sel[r.fila] === "planta" ? PLANTA_TEXTO : lista.find((e) => e.id === sel[r.fila])?.nombre || "";
  const elegir = (nombre) => setSel((s) => ({ ...s, [r.fila]: nombre === PLANTA_TEXTO ? "planta" : lista.find((e) => e.id && e.nombre === nombre)?.id }));
  return (
    <div className="mt-1 space-y-1 text-orange-700" data-testid={`empresa-noid-fila-${r.fila}`}>
      <div>Empresa usuaria no identificada: “{r.empresa?.texto}”{r.empresa?.sugerencias?.length > 0 && ` · posible: ${r.empresa.sugerencias.map((s) => s.nombre).join(", ")}`}</div>
      <div className="w-64"><EmpresaUsuariaSelect value={actual} onChange={elegir} testid={`empresa-fila-select-${r.fila}`} /></div>
    </div>
  );
}

const STATUS_META = {
  nuevo: { label: "Nuevo", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  modificado: { label: "Se actualizará", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  duplicado_bd: { label: "Sin cambios (ya existe)", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  duplicado_roge: { label: "Ya existe en Roge · datos nuevos", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  duplicado_archivo: { label: "Duplicado en archivo", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  revision_identidad: { label: "Revisión manual (ya existe la persona)", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  empresa_no_identificada: { label: "Empresa usuaria no identificada", cls: "bg-orange-50 text-orange-800 border-orange-200" },
  rechazado: { label: "Rechazado", cls: "bg-red-50 text-red-700 border-red-200" },
  con_errores: { label: "Con errores", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function Importaciones() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [purga, setPurga] = useState(null);
  const [purgaLoading, setPurgaLoading] = useState(false);
  const [elegidos, setElegidos] = useState({});
  const [empresasSel, setEmpresasSel] = useState({});
  const inputRef = useRef();

  const loadBatches = () => api.get("/import/batches").then((r) => setBatches(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig(false));
    loadBatches();
  }, []);

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setStep(2);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doConfirm = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/import/confirm", { token: preview.token, complementos: elegidos, empresas: empresasSel });
      setResult(data);
      setStep(3);
      loadBatches();
      toast.success(`Importación completada: ${data.inserted} registros`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const downloadErrors = async () => {
    try { await downloadFile(`/import/${preview.token}/errors`, "errores_importacion.xlsx"); }
    catch { toast.error("No fue posible descargar los errores"); }
  };

  const reset = () => { setStep(1); setFile(null); setPreview(null); setResult(null); setElegidos({}); };

  const purgarCargas = async () => {
    if (!window.confirm("¿Eliminar TODOS los registros cargados por archivo Excel?\n\nLos registros creados manualmente NO se tocan. Los que tengan expediente o contratación se protegen y se reportan.")) return;
    if (!window.confirm("Esta acción NO se puede deshacer. ¿Confirma la eliminación de todos los cargues?")) return;
    setPurgaLoading(true);
    try {
      const { data } = await api.post("/import/purgar-cargas");
      setPurga(data);
      loadBatches();
      toast.success(`Cargues eliminados: ${data.eliminados} registros`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPurgaLoading(false);
    }
  };

  const Steps = () => (
    <div className="flex items-center gap-2 text-sm">
      {[{ n: 1, l: "Subir archivo" }, { n: 2, l: "Revisar" }, { n: 3, l: "Confirmar" }].map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${step >= s.n ? "bg-vino-700 text-white" : "bg-slate-200 text-slate-500"}`}>{s.n}</span>
            <span className={step >= s.n ? "font-medium text-slate-800" : "text-slate-400"}>{s.l}</span>
          </div>
          {i < 2 && <span className="h-px w-8 bg-slate-200" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Importaciones</h1>
        <p className="mt-1 text-sm text-slate-500">Carga inicial de empleados vigentes. Solo control administrativo.</p>
      </div>

      {config && !config.implementation_date && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="aviso-sin-fecha">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <p>
            Debe configurar la <strong>fecha de implementación de Roge</strong> antes de importar.{" "}
            <Link to="/configuracion" className="font-semibold underline">Ir a Configuración</Link>
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-6 flex items-center justify-between">
          <Steps />
          {config?.implementation_date && (
            <span className="hidden text-xs text-slate-400 sm:block">Fecha de implementación: {config.implementation_date}</span>
          )}
        </div>

        {/* PASO 1 */}
        {step === 1 && (
          <div>
            <div
              onClick={() => inputRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center transition-colors hover:border-vino-400 hover:bg-vino-50/40"
              data-testid="zona-subir"
            >
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-vino-50 text-vino-700">
                <UploadCloud className="h-7 w-7" />
              </div>
              <p className="font-display text-lg font-semibold text-slate-800">Subir matriz .xlsx</p>
              <p className="mt-1 text-sm text-slate-500">Seleccione el archivo Excel con la estructura oficial de la matriz</p>
              {file && (
                <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> {file.name}
                </div>
              )}
              <input ref={inputRef} type="file" accept=".xlsx" className="hidden" data-testid="input-archivo" onChange={(e) => setFile(e.target.files[0])} />
            </div>
            <div className="mt-4 rounded-lg border border-vino-100 bg-vino-50/60 p-4 text-sm text-vino-800">
              <p className="font-semibold">Solo control administrativo</p>
              <p className="mt-0.5 text-vino-700">No genera contratos, no abre solicitudes de firma y no envía invitaciones ni correos.</p>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={doPreview}
                disabled={!file || loading || (config && !config.implementation_date)}
                data-testid="btn-revisar"
                className="flex items-center gap-2 rounded-lg bg-vino-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50"
              >
                {loading && <Spinner />} Revisar archivo
              </button>
            </div>
          </div>
        )}

        {/* PASO 2 */}
        {step === 2 && preview && (
          <div className="space-y-5">
            {!preview.structure_ok ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="estructura-invalida">
                  <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                  <div>
                    <p className="font-semibold">Faltan columnas obligatorias: el archivo fue rechazado.</p>
                    <p className="mt-0.5">No se encontraron las columnas obligatorias indicadas. Las columnas opcionales faltantes no bloquean la importación.</p>
                  </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-4 py-2.5 font-semibold">Columna</th>
                        <th className="px-4 py-2.5 font-semibold">Obligatoria esperada</th>
                        <th className="px-4 py-2.5 font-semibold">Se encontró</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.header_errors || []).map((e, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="px-4 py-2 font-mono text-xs text-slate-500">{e.columna} ({e.posicion})</td>
                          <td className="px-4 py-2 text-slate-700">{e.esperado}</td>
                          <td className="px-4 py-2 text-red-600">{e.encontrado}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <>
                {(preview.opcionales_faltantes || []).length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600" data-testid="opcionales-faltantes">
                    <p className="font-semibold text-slate-700">
                      Columnas opcionales sin información en el archivo ({preview.opcionales_faltantes.length})
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {preview.opcionales_faltantes.map((o) => (
                        <span key={o.posicion} className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] text-slate-500">
                          {o.columna} · {o.encabezado} — Opcional · sin información
                        </span>
                      ))}
                    </div>
                    <p className="mt-2 text-slate-400">No bloquean la importación: se guardarán como vacías.</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                  {[
                    { l: "Filas", v: preview.summary.total_filas, c: "text-slate-800" },
                    { l: "Nuevos", v: preview.summary.importables, c: "text-emerald-600" },
                    { l: "Se actualizarán", v: preview.summary.modificados ?? 0, c: "text-blue-600" },
                    { l: "Sin cambios", v: preview.summary.duplicados, c: "text-amber-600" },
                    { l: "Ya en Roge", v: preview.summary.duplicados_roge ?? 0, c: "text-violet-600" },
                    { l: "Revisión manual", v: (preview.summary.revision_identidad ?? 0) + (preview.summary.empresa_no_identificada ?? 0), c: "text-orange-600" },
                    { l: "Rechazados", v: preview.summary.rechazados, c: "text-red-600" },
                    { l: "Con errores", v: preview.summary.con_errores, c: "text-red-600" },
                  ].map((s) => (
                    <div key={s.l} className="rounded-lg border border-slate-200 bg-white p-3 text-center">
                      <p className={`font-display text-2xl font-bold ${s.c}`}>{s.v}</p>
                      <p className="text-xs text-slate-500">{s.l}</p>
                    </div>
                  ))}
                </div>

                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full min-w-[720px] text-sm" data-testid="tabla-preview">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-3 py-2.5 font-semibold">Fila</th>
                        <th className="px-3 py-2.5 font-semibold">Nombre</th>
                        <th className="px-3 py-2.5 font-semibold">Cédula</th>
                        <th className="px-3 py-2.5 font-semibold">Empresa</th>
                        <th className="px-3 py-2.5 font-semibold">Ingreso</th>
                        <th className="px-3 py-2.5 font-semibold">Estado</th>
                        <th className="px-3 py-2.5 font-semibold">Observaciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(preview.rows || []).map((r) => {
                        const m = STATUS_META[r.status] || STATUS_META.nuevo;
                        return (
                          <tr key={r.fila} className="border-b border-slate-100 last:border-0">
                            <td className="px-3 py-2 font-mono text-xs text-slate-400">{r.fila}</td>
                            <td className="px-3 py-2 font-medium text-slate-800">{r.resumen.nombre_completo || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">{r.resumen.cedula || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">{r.resumen.empresa_cliente || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">{r.resumen.fecha_ingreso || "—"}</td>
                            <td className="px-3 py-2"><span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${m.cls}`}>{m.label}</span></td>
                            <td className="px-3 py-2 text-xs text-red-600">
                              {(r.errores || []).map((e, i) => <div key={i}>{e.columna}: {e.msg}</div>)}
                              {r.motivo && <div className="text-orange-700" data-testid={`motivo-fila-${r.fila}`}>{r.motivo}</div>}
                              {r.status === "empresa_no_identificada" && <EmpresaFila r={r} sel={empresasSel} setSel={setEmpresasSel} />}
                              {r.status === "modificado" && (
                                <div className="text-blue-600" data-testid={`cambios-fila-${r.fila}`}>
                                  {r.total_cambios} campo{r.total_cambios !== 1 ? "s" : ""}: {(r.cambios || []).map((c) => c.campo).join(", ")}{r.total_cambios > (r.cambios || []).length ? "…" : ""}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <DuplicadosRoge rows={(preview.rows || []).filter((r) => r.status === "duplicado_roge" || (r.roge?.conflictos || []).length)} elegidos={elegidos} setElegidos={setElegidos} />
                <p className="text-xs text-slate-500">
                  Se crearán los registros <strong>Nuevos</strong> y se actualizarán los existentes que traigan
                  <strong> modificaciones</strong> (queda en su historial). Los registros creados por <strong>Roge</strong> nunca se reemplazan ni se duplican: solo se completan los campos vacíos que marques. Los duplicados sin cambios, rechazados y con errores no se tocan.
                </p>
              </>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3">
              <button onClick={reset} className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50" data-testid="btn-volver-subir">
                <ArrowLeft className="h-4 w-4" /> Volver
              </button>
              <div className="flex gap-2">
                {(preview.header_errors.length > 0 || preview.summary.con_errores > 0 || preview.summary.rechazados > 0) && (
                  <button onClick={downloadErrors} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="btn-descargar-errores">
                    <Download className="h-4 w-4" /> Descargar errores
                  </button>
                )}
                {preview.structure_ok && (
                  <button onClick={doConfirm} disabled={loading || (preview.summary.importables === 0 && (preview.summary.modificados ?? 0) === 0 && (preview.summary.duplicados_roge ?? 0) === 0 && !Object.values(empresasSel).some(Boolean))} data-testid="btn-confirmar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-5 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">
                    {loading && <Spinner />} Confirmar carga ({preview.summary.importables} nuevos{(preview.summary.modificados ?? 0) > 0 ? ` · ${preview.summary.modificados} actualizaciones` : ""}{(preview.summary.duplicados_roge ?? 0) > 0 ? ` · ${preview.summary.duplicados_roge} completar en Roge` : ""})
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PASO 3 */}
        {step === 3 && result && (
          <div className="flex flex-col items-center py-8 text-center" data-testid="resultado-importacion">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <CheckCircle2 className="h-9 w-9" />
            </div>
            <h3 className="font-display text-xl font-bold text-slate-900">Importación completada</h3>
            <p className="mt-1 text-sm text-slate-500">
              Se crearon {result.inserted} vinculaciones nuevas{(result.updated ?? 0) > 0 ? ` y se actualizaron ${result.updated} existentes` : ""}.
            </p>
            <div className="mt-6 grid w-full max-w-lg grid-cols-2 gap-3 text-left sm:grid-cols-5">
              {[
                { l: "Importados", v: result.batch?.resultado?.importados ?? 0 },
                { l: "Actualizados", v: result.batch?.resultado?.modificados ?? 0 },
                { l: "Sin cambios", v: result.batch?.resultado?.duplicados_omitidos ?? 0 },
                { l: "Completados en Roge", v: result.batch?.resultado?.roge_completados ?? 0 },
                { l: "Rechazados", v: result.batch?.resultado?.rechazados ?? 0 },
                { l: "Con errores", v: result.batch?.resultado?.con_errores ?? 0 },
              ].map((s) => (
                <div key={s.l} className="rounded-lg border border-slate-200 bg-white p-3 text-center">
                  <p className="font-display text-xl font-bold text-slate-800">{s.v}</p>
                  <p className="text-[11px] text-slate-500">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={reset} className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <RefreshCw className="h-4 w-4" /> Nueva importación
              </button>
              <Link to="/empleados" className="rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800">Ver empleados</Link>
            </div>
          </div>
        )}
      </div>

      {/* Historial de importaciones */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <History className="h-5 w-5 text-vino-700" />
          <h2 className="font-display text-lg font-semibold text-slate-900">Trazabilidad de importaciones</h2>
        </div>
        {batches.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">Aún no se han realizado importaciones.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" data-testid="tabla-batches">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 font-semibold">Fecha</th>
                  <th className="px-3 py-2 font-semibold">Usuario</th>
                  <th className="px-3 py-2 font-semibold">Archivo</th>
                  <th className="px-3 py-2 font-semibold">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2 text-slate-600">{b.fecha ? new Date(b.fecha).toLocaleString("es-CO") : "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{b.user_email}</td>
                    <td className="px-3 py-2 text-slate-600">{b.filename}</td>
                    <td className="px-3 py-2 text-slate-600">
                      {b.resultado?.importados ?? 0} importados{(b.resultado?.modificados ?? 0) > 0 ? ` · ${b.resultado.modificados} actualizados` : ""} · {b.resultado?.duplicados_omitidos ?? 0} sin cambios · {b.resultado?.rechazados ?? 0} rechazados
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Zona de riesgo: solo administradores */}
      {user?.role === "admin" && (
        <div className="rounded-xl border border-red-200 bg-red-50/40 p-5" data-testid="zona-purga">
          <div className="mb-2 flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            <h2 className="font-display text-lg font-semibold text-red-800">Zona de riesgo</h2>
          </div>
          <p className="mb-4 text-sm text-red-700">
            Elimina <strong>todos los registros cargados por archivo Excel</strong> y la trazabilidad de los cargues.
            Los registros creados manualmente no se tocan; los que tengan expediente o contratación se protegen.
            Esta acción no se puede deshacer.
          </p>
          <button
            onClick={purgarCargas}
            disabled={purgaLoading}
            data-testid="btn-purgar-cargas"
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {purgaLoading ? <Spinner /> : <Trash2 className="h-4 w-4" />} Eliminar todos los cargues
          </button>
          {purga && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-sm" data-testid="resultado-purga">
              <p className="font-semibold text-slate-800">
                {purga.eliminados} registros eliminados · {purga.protegidos.length} protegidos
              </p>
              {purga.protegidos.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Protegidos (tienen expediente o contratación):</p>
                  <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-xs text-slate-600">
                    {purga.protegidos.map((p, i) => <li key={i}>• {p.nombre} ({p.cedula || "sin cédula"})</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
